/**
 * Convert WebAuthn / SimpleWebAuthn errors into operator-friendly
 * messages. Browser-thrown `DOMException`s carry W3C-spec text like
 * "The operation either timed out or was not allowed. See:
 * https://www.w3.org/TR/webauthn-2/#sctn-privacy-considerations-client"
 * — accurate but useless to anyone who isn't writing a passkey demo.
 *
 * The `name` field is the right discriminator (it's stable across
 * browsers). SimpleWebAuthn's `WebAuthnError` wrapper also surfaces
 * the underlying DOMException on `.cause`, but the name on the outer
 * error is set to match — checking `name` covers both layers.
 */
export function humanizeWebAuthnError(err: unknown, mode: 'register' | 'authenticate'): string {
  const name =
    err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : '';
  switch (name) {
    case 'NotAllowedError':
    case 'AbortError':
      return mode === 'register'
        ? 'Passkey setup was cancelled or timed out. Click "Add passkey" to try again.'
        : 'Passkey sign-in was cancelled or timed out. Try again, or use your authenticator app code instead.';
    case 'InvalidStateError':
      return 'This device is already registered as a passkey for your account.';
    case 'NotSupportedError':
      return "This device doesn't support passkeys. Try a different browser, or use the authenticator app code instead.";
    case 'SecurityError':
      return "Couldn't verify the site origin for the passkey prompt. Reload the page and try again.";
    case 'ConstraintError':
      return 'The authenticator refused this passkey type. Try a different device or use the authenticator app code instead.';
    default: {
      // Surface the message when it's short and looks like a real
      // error description; otherwise fall back to a generic note so
      // we never echo the W3C spec URL at the operator.
      const msg = err instanceof Error ? err.message : '';
      if (msg && msg.length < 80 && !msg.includes('webauthn')) return msg;
      return mode === 'register'
        ? 'Could not register the passkey. Try again, and check that your browser allows the prompt.'
        : 'Could not sign in with the passkey. Try again, or use your authenticator app code instead.';
    }
  }
}
